import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Target, Lightbulb, Users, AlertTriangle, Eye, 
  TrendingUp, Shield, CheckCircle2, XCircle
} from 'lucide-react';
import { PdfButton } from './report/ReportBlocks';

const AnalysisCard = ({ icon: Icon, title, children, color = 'primary' }) => (
  <Card className="h-full hover:shadow-md transition-shadow">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Icon className={`h-4 w-4 text-${color}`} strokeWidth={1.5} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {children}
    </CardContent>
  </Card>
);

const SkillBadge = ({ skill, type = 'default' }) => {
  const variants = {
    mandatory: 'bg-primary/10 text-primary border-primary/20',
    optional: 'bg-muted text-muted-foreground border-muted',
    default: 'bg-secondary text-secondary-foreground',
  };
  
  return (
    <Badge variant="outline" className={`${variants[type]} text-xs`}>
      {skill}
    </Badge>
  );
};

const RiskItem = ({ risk, severity }) => {
  const severityColors = {
    High: 'text-red-500 bg-red-500/10',
    Medium: 'text-yellow-500 bg-yellow-500/10',
    Low: 'text-green-500 bg-green-500/10',
  };
  
  return (
    <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50">
      <Badge className={`${severityColors[severity] || severityColors.Medium} text-xs shrink-0`}>
        {severity}
      </Badge>
      <span className="text-sm text-foreground">{risk}</span>
    </div>
  );
};

export const JDAnalysisPanel = ({ analysis, isLoading, pdfUrl }) => {
  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="jd-analysis-loading">
        <p className="text-sm text-muted-foreground flex items-center gap-2" data-testid="jd-analysis-loading-text">
          <span className="h-2 w-2 rounded-full bg-indigo-500 animate-pulse" /> AI is analyzing this job description — usually under a minute
        </p>
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (!analysis || analysis.error) {
    return null;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="jd-analysis-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-primary" strokeWidth={1.5} />
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
            JD Intelligence Analysis
          </h2>
        </div>
        {pdfUrl && <PdfButton url={pdfUrl} testid="jd-download-pdf-btn" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
        {/* Role Summary */}
        <AnalysisCard icon={Target} title="Role Summary" color="primary">
          <p className="text-sm text-muted-foreground leading-relaxed">
            {analysis.role_summary || 'No summary available'}
          </p>
        </AnalysisCard>

        {/* Mandatory Skills */}
        <AnalysisCard icon={CheckCircle2} title="Mandatory Skills" color="green-500">
          <div className="flex flex-wrap gap-1.5">
            {(analysis.mandatory_skills || []).map((skill, idx) => (
              <SkillBadge key={idx} skill={skill} type="mandatory" />
            ))}
            {(!analysis.mandatory_skills || analysis.mandatory_skills.length === 0) && (
              <span className="text-sm text-muted-foreground">Not specified</span>
            )}
          </div>
        </AnalysisCard>

        {/* Good to Have */}
        <AnalysisCard icon={Lightbulb} title="Good to Have" color="yellow-500">
          <div className="flex flex-wrap gap-1.5">
            {(analysis.good_to_have_skills || []).map((skill, idx) => (
              <SkillBadge key={idx} skill={skill} type="optional" />
            ))}
            {(!analysis.good_to_have_skills || analysis.good_to_have_skills.length === 0) && (
              <span className="text-sm text-muted-foreground">Not specified</span>
            )}
          </div>
        </AnalysisCard>

        {/* Seniority & Culture */}
        <AnalysisCard icon={TrendingUp} title="Seniority & Culture" color="purple-500">
          <div className="space-y-3">
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Level</span>
              <p className="text-sm font-medium">{analysis.seniority_expectation || 'Not specified'}</p>
            </div>
            <div>
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Culture</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {(analysis.culture_indicators || []).map((indicator, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {indicator}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </AnalysisCard>

        {/* Hidden Expectations */}
        <AnalysisCard icon={Eye} title="Hidden Expectations" color="orange-500">
          {analysis.hidden_expectations ? (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Stakeholder Mgmt</span>
                <Badge variant="outline" className="text-xs">
                  {analysis.hidden_expectations.stakeholder_management || 'N/A'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Ownership</span>
                <Badge variant="outline" className="text-xs">
                  {analysis.hidden_expectations.ownership_level || 'N/A'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Timezone Flex</span>
                <Badge variant="outline" className="text-xs">
                  {analysis.hidden_expectations.timezone_flexibility || 'N/A'}
                </Badge>
              </div>
              {analysis.hidden_expectations.communication_expectations && (
                <p className="text-xs text-muted-foreground mt-2 pt-2 border-t">
                  {analysis.hidden_expectations.communication_expectations}
                </p>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">No hidden expectations detected</span>
          )}
        </AnalysisCard>

        {/* Risk Areas */}
        <AnalysisCard icon={AlertTriangle} title="Hiring Risk Areas" color="red-500">
          <div className="space-y-2">
            {(analysis.risk_areas || []).map((risk, idx) => (
              <RiskItem key={idx} risk={risk.risk} severity={risk.severity} />
            ))}
            {(!analysis.risk_areas || analysis.risk_areas.length === 0) && (
              <div className="flex items-center gap-2 text-green-500">
                <Shield className="h-4 w-4" />
                <span className="text-sm">No significant risks identified</span>
              </div>
            )}
          </div>
        </AnalysisCard>
      </div>
    </div>
  );
};

export default JDAnalysisPanel;
